import { Rank } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/** EB-4 — BUNKER. Les deux verrous, l'un après l'autre.
 *
 *  Le bestiaire enseigne les deux défenses séparément : le Kyste impose un ORDRE
 *  (crève la coque, puis frappe), l'Égide impose un ANGLE (contourne, puis
 *  frappe). Le Bunker est le premier à les EMPILER, et dans un ordre qui n'est
 *  pas négociable.
 *
 *    1. Deux casemates blindées scellent le noyau (`shieldedByParts`) : tant
 *       qu'une tient, rien ne l'atteint, d'aucun côté. On DÉMONTE d'abord.
 *    2. Le noyau ainsi exposé porte un cône frontal (`shieldHp` + cône) : il
 *       n'est vulnérable que par le flanc. On CONTOURNE ensuite.
 *
 *  ── Ce qu'il enseigne ──
 *  Qu'une compétence maîtrisée n'en dispense pas d'une autre, et qu'un combat
 *  peut CHANGER DE NATURE en cours de route. La première moitié est un problème
 *  de priorité (quelle casemate d'abord, laquelle me gêne le plus), résolu en
 *  réfléchissant ; la seconde est un problème de placement, résolu en se
 *  déplaçant. Le joueur qui n'a intégré qu'une des deux leçons cale à la moitié :
 *  il perce les casemates puis s'acharne de face sur un noyau qui absorbe tout,
 *  ou il cherche l'angle d'un noyau encore scellé qui ne le lui rendra pas. C'est
 *  l'examen de sortie des blindés, celui qui vérifie que les deux réflexes
 *  coexistent.
 *
 *  ── Pourquoi il tangue lentement au lieu de foncer ──
 *  Deux verrous à forcer demandent du temps ; un ennemi rapide ne le laisserait
 *  pas et l'enchaînement démontage → contournement se lirait comme de la
 *  précipitation, pas comme une séquence. Posté et lent, le Bunker donne à chaque
 *  moitié le temps d'être comprise. Il BLOQUE de fait la progression — on ne
 *  l'ignore pas, on le résout — ce qui justifie son coût total (680 PV de
 *  casemates, 480 de corps, 600 de cône) : il rend plus qu'il ne coûte en verrous
 *  franchis et en leçon donnée. */
export default defineEnemy({
  id: 'bunker',
  rank: Rank.Heavy,
  hp: 480,
  visualRadius: 46,
  score: 980,
  telegraph: 1.1,
  drops: 5,
  // Pillbox lent : il prend position et se déhanche à peine. Assez pour que
  // l'angle de flanc à trouver ne soit jamais tout à fait le même, jamais assez
  // pour s'échapper avant qu'on ait forcé ses deux verrous.
  motion: { kind: Motion.Hover, speed: 200, amplitude: 34, frequency: 0.35, holdY: 300 },
  // VERROU 1 — les casemates scellent le noyau (voir bunker-casemates).
  shieldedByParts: true,
  parts: [
    { enemy: 'bunker-casemate-g', offX: -60, offY: 4 },
    { enemy: 'bunker-casemate-d', offX: 60, offY: 4 },
  ],
  // VERROU 2 — une fois les casemates tombées, le noyau exposé ne se laisse
  // toucher que hors de son cône frontal. ±58° : une épaule étroite, dans la
  // lignée de l'Égide, pour que le contournement reste un geste et non un mur.
  shieldHp: 600,
  shieldConeDeg: 58,
  emitter: {
    // Le noyau tire même scellé — « intouchable » n'est pas « inoffensif ». Un
    // éventail statique clairsemé : une pression d'aire pendant le démontage, qui
    // ne se change pas en rideau une fois le noyau à nu (l'enjeu de la seconde
    // moitié est le placement, pas l'esquive).
    pattern: Pattern.Fan,
    every: 2.4,
    count: 4,
    spreadDeg: 72,
    speed: 320,
    firstDelay: 2.2,
    family: Fam.EnemyRed,
  },
});
